/**
 * Comprehensive sample conversation data for UI testing.
 * Exercises: multi-intent tags, compound sub-actions, director notes,
 * dice rolls, state changes, validity checks, OOC/system messages,
 * multi-agent replies with avatars, sub-agent summaries, credits, tokens.
 */

export interface SampleMsg {
  id: string;
  adventure_id: string;
  role: "user" | "narrator";
  content: string;
  turn: number;
  created_at: string;
  player_id?: string;
  metadata?: Record<string, any>;
}

// ── Entity avatars for narrator entity rendering ──

export const SAMPLE_ENTITIES: Map<string, { name: string; image_url: string | null; type?: string; emotional_state?: { current_mood?: string; stress_level?: number } }> = new Map([
  ["elara", { name: "Elara Windwalker", image_url: null, type: "npc", emotional_state: { current_mood: "worried", stress_level: 65 } }],
  ["thorne", { name: "Thorne Ironveil", image_url: null, type: "npc", emotional_state: { current_mood: "suspicious", stress_level: 40 } }],
  ["grizzle", { name: "Grizzle the Innkeeper", image_url: null, type: "npc", emotional_state: { current_mood: "cheerful", stress_level: 10 } }],
  ["shadow", { name: "The Shadow Merchant", image_url: null, type: "npc", emotional_state: { current_mood: "enigmatic", stress_level: 30 } }],
  ["ravenwood", { name: "Ravenwood Forest", image_url: null, type: "location" }],
  ["silver_tav", { name: "The Silver Tankard Inn", image_url: null, type: "location" }],
]);

// ═══════════════════════════════════════════════════════════
// TURN 1 — Simple opening
// ═══════════════════════════════════════════════════════════

export const SAMPLE_MESSAGES: SampleMsg[] = [
  // ── Turn 1: Narrator opening scene ──
  {
    id: "msg-001",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `The rain hammers against the frosted windows of **The Silver Tankard Inn**, each drop a tiny drumbeat in the symphony of the storm. The common room is thick with the smell of wet wool, spilt ale, and woodsmoke curling lazily from a great stone hearth.

A handful of travelers huddle at scarred oak tables. In the corner, a hooded figure traces circles on the tabletop with a gloved finger — slow, deliberate, watchful. Behind the bar, **Grizzle the Innkeeper** polishes the same pewter mug he's been working on for the last hour, his eyes darting toward the stranger every few seconds.

The fire pops. A log shifts. Someone coughs.

Outside, the road to Ravenwood has turned to mud. You're not going anywhere tonight.`,
    turn: 1,
    created_at: "2026-06-20T10:00:00Z",
    metadata: {
      is_scene_intro: true,
      credits_used: 0.0182,
      input_tokens: 450,
      output_tokens: 320,
      models: ["google/gemini-2.5-flash"],
      scene_image_url: null,
      state_changes: { current_location: "The Silver Tankard Inn" },
    },
  },

  // ── Turn 2: Player asks a question ──
  {
    id: "msg-002",
    adventure_id: "test-adventure",
    role: "user",
    content: "I study the hooded figure in the corner. Who are they? What do I notice about them?",
    turn: 2,
    created_at: "2026-06-20T10:01:00Z",
    metadata: {
      intent_classification: {
        primary_intent: "EXPLORATION",
        secondary_intents: ["QUESTION"],
        advances_game: true,
        requires_dice: true,
        requires_tools: ["skill_check"],
        sub_actions: [],
        echo_risk: false,
        is_inspection: true,
        is_planning: false,
        inspection_target: "hooded figure",
        reasoning: "Player is examining an NPC — this is observation/inspection, not dialogue. Secondary QUESTION tag because they're asking 'who are they'.",
      },
    },
  },

  // ── Turn 2 response: Exploration with dice roll ──
  {
    id: "msg-003",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `You narrow your eyes, letting the firelight play across the stranger's silhouette.

The hood is deep, but not deep enough. As they turn slightly toward the hearth, you catch the glint of a silver chain around their neck — a sigil you don't recognize. Their gloves are fine leather, well-kept, and one finger taps a rhythm that feels almost... deliberate. Like a code.

Beneath the table, you spot the edge of a scabbard. Not a common blade — the pommel catches the light with an inlay of **onyx and gold**.

Your instincts scream one thing: this is no ordinary traveler.`,
    turn: 2,
    created_at: "2026-06-20T10:01:15Z",
    metadata: {
      intent_classification: {
        primary_intent: "EXPLORATION",
        secondary_intents: ["QUESTION"],
      },
      credits_used: 0.0221,
      input_tokens: 520,
      output_tokens: 280,
      models: ["google/gemini-2.5-flash"],
      dice_rolls: [
        { expression: "1d20+3", result: 17, reason: "Perception check to observe the hooded figure" },
      ],
      mechanic_results: [{ tool: "skill_check", result: { skill: "perception", roll: 17, dc: 12, success: true, details: "Noticed sigil, fine gloves, and onyx-gold pommel" } }],
      agents_used: ["state", "world", "narrator", "memory"],
      tools_called: 2,
    },
  },

  // ── Turn 3: Player compound action (multi-intent) ──
  {
    id: "msg-004",
    adventure_id: "test-adventure",
    role: "user",
    content: "I approach the bar and order a drink from Grizzle, then casually ask him about the stranger. While talking, I check my coin pouch — I plan to offer the stranger a drink later.",
    turn: 3,
    created_at: "2026-06-20T10:02:00Z",
    metadata: {
      intent_classification: {
        primary_intent: "COMPOUND",
        secondary_intents: ["SOCIAL", "ITEM_USE", "PLANNING"],
        advances_game: true,
        requires_dice: false,
        is_planning: true,
        sub_actions: [
          { intent: "MOVEMENT", text: "Approach the bar" },
          { intent: "SOCIAL", text: "Order a drink from Grizzle" },
          { intent: "DIALOGUE", text: "Ask about the stranger" },
          { intent: "ITEM_USE", text: "Check coin pouch" },
          { intent: "PLANNING", text: "Plan to offer stranger a drink" },
        ],
        reasoning: "Five sequential actions: movement, social, dialogue, item check, planning. Primary COMPOUND with secondary tags for each facet. PLANNING flag set for the future commitment.",
      },
    },
  },

  // ── Turn 3 response: Multi-agent narrator with NPC sub-agent responses ──
  {
    id: "msg-005",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `You push back your chair and cross the creaking floorboards to the bar.

Grizzle looks up from his eternal mug-polishing and breaks into a gap-toothed grin. "Aye, traveler! What'll it be? The house ale's strong enough to strip paint, but it'll warm your bones."

**Grizzle the Innkeeper** leans in conspiratorially as he pours. "That one in the corner?" His voice drops. "Came in just before the storm hit. Paid in silver — *old* silver. Didn't touch his drink. Just... sat there. I'd steer clear if I were you. Folk like that don't travel through Ravenwood for the scenery."

You pat your coin pouch — twelve gold and a handful of copper. Enough for a round and then some. The plan forms in your mind: a drink as an olive branch.`,
    turn: 3,
    created_at: "2026-06-20T10:02:30Z",
    metadata: {
      intent_classification: {
        primary_intent: "COMPOUND",
        secondary_intents: ["SOCIAL", "ITEM_USE", "PLANNING"],
        is_planning: true,
      },
      director_notes: {
        narrative_guidance: "Grizzle should be warm but wary — he's seen strange things on stormy nights. Have him share one specific rumor about the stranger. The player's planning to buy the stranger a drink sets up a future social encounter — plant that seed.",
        pacing_transition: null,
        npc_intents: [
          { name: "Grizzle", intent: "Warn the player about the suspicious stranger while being hospitable" },
        ],
        advance_turn: false,
      },
      credits_used: 0.0356,
      input_tokens: 680,
      output_tokens: 410,
      models: ["google/gemini-2.5-flash"],
      agents_used: ["state", "world", "narrator", "memory", "character_grizzle"],
      tools_called: 3,
      sub_agent_summary: JSON.stringify({
        character_grizzle: { model: "google/gemini-2.5-flash-lite", execution_ms: 1240, confidence: 0.92, tokens: 180 },
        state: { execution_ms: 85, tools: ["update_state"], actions: 1 },
        narrator: { model: "google/gemini-2.5-flash", execution_ms: 2850, confidence: 0.95, tokens: 410 },
      }),
      state_changes: {
        current_location: "The Silver Tankard Inn (at the bar)",
        inventory_changes: [{ item: "gold", delta: 0, total: 12 }],
      },
      total_execution_ms: 4320,
    },
  },

  // ── Turn 4: Player dialogue with NPC ──
  {
    id: "msg-006",
    adventure_id: "test-adventure",
    role: "user",
    content: "\"Rough night for travel.\" I carry two mugs toward the hooded figure and set one down at their table. \"Name's Arden. Mind if I sit?\"",
    turn: 4,
    created_at: "2026-06-20T10:03:00Z",
    metadata: {
      intent_classification: {
        primary_intent: "DIALOGUE",
        secondary_intents: ["SOCIAL"],
        advances_game: true,
        requires_dice: false,
        echo_risk: true,
        is_inspection: false,
        reasoning: "Player is speaking in-character to the hooded NPC. Echo risk HIGH because the narrator must NOT paraphrase the player's dialogue — show the NPC's reaction instead.",
      },
    },
  },

  // ── Turn 4 response: NPC reply via character agent ──
  {
    id: "msg-007",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `The figure doesn't look up immediately. The gloved finger stops its rhythm.

Then, slowly, the hood tilts back just enough to reveal a sharp jawline and eyes the color of **storm clouds** — grey, flecked with silver. A woman. Her gaze flicks to the mug, then to you.

**Elara Windwalker** slides the mug closer with one finger but doesn't drink. "Arden," she repeats, tasting the name. "You're either very brave or very new to these parts. Most people in this inn have been pretending I don't exist for the last three hours."

A thin smile plays at the corner of her mouth. "Sit. But keep your voice down. The trees have ears in Ravenwood."`,
    turn: 4,
    created_at: "2026-06-20T10:03:25Z",
    metadata: {
      intent_classification: { primary_intent: "DIALOGUE", secondary_intents: ["SOCIAL"], echo_risk: true },
      director_notes: {
        narrative_guidance: "Elara is guarded but intrigued. She hasn't spoken to anyone in hours. Use her dialogue to drop world-building: the forest is dangerous, she's being hunted or watching for something. Reveal her name naturally.",
        npc_intents: [
          { name: "Elara", intent: "Assess whether Arden is a threat or a potential ally — she needs help but trusts no one" },
        ],
        advance_turn: false,
      },
      credits_used: 0.0289,
      input_tokens: 590,
      output_tokens: 350,
      models: ["google/gemini-2.5-flash"],
      agents_used: ["state", "world", "narrator", "memory", "character_elara"],
      tools_called: 2,
      sub_agent_summary: JSON.stringify({
        character_elara: { model: "google/gemini-2.5-flash", execution_ms: 1680, confidence: 0.94, tokens: 250 },
        narrator: { model: "google/gemini-2.5-flash", execution_ms: 2200, confidence: 0.96, tokens: 350 },
      }),
      total_execution_ms: 4110,
    },
  },

  // ── Turn 5: OOC / META message ──
  {
    id: "msg-008",
    adventure_id: "test-adventure",
    role: "user",
    content: "OOC: What are my current stats and inventory? I want to assess my situation before continuing.",
    turn: 5,
    created_at: "2026-06-20T10:04:00Z",
    metadata: {
      is_ooc: true,
      intent_classification: {
        primary_intent: "META",
        secondary_intents: [],
        advances_game: false,
        requires_dice: false,
        reasoning: "Player is asking out-of-character for game state information.",
      },
    },
  },

  // ── Turn 5 response: System / OOC reply ──
  {
    id: "msg-009",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `📊 **Character Status — Arden**

| Stat | Value |
|---|---|
| HP | 24/28 |
| Level | 3 (Ranger) |
| Armor Class | 15 |
| Gold | 12 |
| Condition | Normal |

**Inventory:**
- Longbow (equipped)
- Quiver (18 arrows)
- Dagger
- Leather armor (worn)
- Rope (50 ft)
- Tinderbox
- Waterskin
- Healing herbs (2 uses)

**Active effects:** none
**Current location:** The Silver Tankard Inn`,
    turn: 5,
    created_at: "2026-06-20T10:04:05Z",
    metadata: {
      is_ooc: true,
      intent_classification: { primary_intent: "META" },
      credits_used: 0.0021,
      input_tokens: 120,
      output_tokens: 200,
    },
  },

  // ── Turn 6: Player combat action ──
  {
    id: "msg-010",
    adventure_id: "test-adventure",
    role: "user",
    content: "I draw my bow and fire an arrow at the creature that just smashed through the inn window!",
    turn: 6,
    created_at: "2026-06-20T10:05:00Z",
    metadata: {
      intent_classification: {
        primary_intent: "COMBAT",
        secondary_intents: ["ACTION"],
        advances_game: true,
        requires_dice: true,
        requires_tools: ["skill_check", "roll_dice"],
        reasoning: "Player is initiating combat against an unexpected threat.",
      },
    },
  },

  // ── Turn 6 response: Combat with multiple dice rolls ──
  {
    id: "msg-011",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `Glass explodes across the common room as the window shatters inward. A twisted shape — part wolf, part shadow — lands on an overturned table, its maw dripping with something darker than saliva.

Your bow is in your hands before you finish thinking. The arrow sings through the smoky air and buries itself in the creature's shoulder. It howls — a sound that rattles the pewter mugs on their hooks.

**Elara** is on her feet, a blade already in her hand. "The eyes!" she shouts. "Aim for the eyes!"

**Grizzle** ducks behind the bar, grabbing a rusted crossbow from beneath the counter. "Not in my inn, you bastard!"`,
    turn: 6,
    created_at: "2026-06-20T10:05:18Z",
    metadata: {
      intent_classification: { primary_intent: "COMBAT", secondary_intents: ["ACTION"] },
      director_notes: {
        narrative_guidance: "Action-heavy combat scene. Keep it visceral and fast. Show NPC reactions — Elara fights alongside the player, Grizzle provides comedic relief with his crossbow.",
        pacing_transition: { new_phase: "combat", reason: "Shadow creature attacks the inn" },
        inject_encounter: { type: "combat", description: "A shadow-wolf creature smashes through the inn window", difficulty: "moderate" },
        npc_intents: [
          { name: "Elara", intent: "Fight the shadow creature alongside Arden — she knows its weakness" },
          { name: "Grizzle", intent: "Defend his inn with his old crossbow despite being terrified" },
        ],
        advance_turn: false,
      },
      dice_rolls: [
        { expression: "1d20+6", result: 19, reason: "Longbow attack vs shadow wolf AC 14" },
        { expression: "1d8+3", result: 10, reason: "Arrow damage (piercing)" },
      ],
      mechanic_results: [
        { tool: "skill_check", result: { skill: "archery", roll: 19, dc: 14, success: true, critical: false } },
        { tool: "roll_dice", result: { expression: "1d8+3", total: 10, type: "damage" } },
      ],
      credits_used: 0.0412,
      input_tokens: 720,
      output_tokens: 480,
      models: ["google/gemini-2.5-flash"],
      agents_used: ["state", "combat", "world", "narrator", "memory", "character_elara", "character_grizzle"],
      tools_called: 5,
      sub_agent_summary: JSON.stringify({
        combat: { execution_ms: 340, tools: ["roll_dice", "apply_damage"], result: "10 damage to shadow wolf" },
        character_elara: { model: "google/gemini-2.5-flash-lite", execution_ms: 920, confidence: 0.91, tokens: 140 },
        character_grizzle: { model: "google/gemini-2.5-flash-lite", execution_ms: 780, confidence: 0.88, tokens: 110 },
        narrator: { model: "google/gemini-2.5-flash", execution_ms: 3100, confidence: 0.94, tokens: 480 },
      }),
      state_changes: {
        combatants: [{ name: "Shadow Wolf", hp: 35, max_hp: 45, conditions: ["wounded"] }],
      },
      total_execution_ms: 5340,
    },
  },

  // ── Turn 7: Validity check — player tries something impossible ──
  {
    id: "msg-012",
    adventure_id: "test-adventure",
    role: "user",
    content: "I pull out my magic wand and cast Fireball on the creature!",
    turn: 7,
    created_at: "2026-06-20T10:06:00Z",
    metadata: {
      intent_classification: {
        primary_intent: "COMBAT",
        secondary_intents: ["ITEM_USE"],
        advances_game: true,
        requires_dice: true,
        reasoning: "Player attempts to use a magic wand that does not exist in their inventory.",
      },
      validity_check: {
        is_valid: false,
        violation_type: "invented_item",
        violation_details: "Player claims to have a 'magic wand' but inventory contains: longbow, quiver, dagger, leather armor, rope, tinderbox, waterskin, healing herbs. No wand exists and no magic_system mechanic is active.",
        narrative_correction: "Arden reaches for something at his belt — but his hand finds only the worn leather of an empty sheath he's never used for a wand. The wish fizzles as quickly as it formed. Grizzle glances over: 'Wrong tavern for wizards, lad!'",
      },
    },
  },

  // ── Turn 7 response: Humorous anti-cheat rejection ──
  {
    id: "msg-013",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `You reach for your belt, fingers grasping for a wand that... isn't there. Your hand closes on empty air. For a brief, embarrassing moment, you stand there with your arm extended, pointing dramatically at a shadow-wolf that is very much not on fire.

The creature tilts its head. Even it seems confused.

**Grizzle** barks a laugh from behind the bar. "Wrong tavern for wizards, lad! Try the Oak and Parlor two towns over — they get the fancy types." He levels his crossbow. "Now use that bow of yours before it eats someone!"`,
    turn: 7,
    created_at: "2026-06-20T10:06:10Z",
    metadata: {
      intent_classification: { primary_intent: "COMBAT", secondary_intents: ["ITEM_USE"] },
      validity_check: {
        is_valid: false,
        violation_type: "invented_item",
        violation_details: "No magic wand in inventory, no magic_system active",
      },
      credits_used: 0.0189,
      input_tokens: 380,
      output_tokens: 260,
      models: ["google/gemini-2.5-flash"],
      agents_used: ["narrator", "character_grizzle"],
      tools_called: 1,
    },
  },

  // ── Turn 8: System message (dead turn correction) ──
  {
    id: "msg-014",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `⚙️ [System] The previous action was corrected by the Game Master. The shadow-wolf combat continues. Elara has wounded the creature's flank. Your next action may end this fight.`,
    turn: 8,
    created_at: "2026-06-20T10:06:30Z",
    metadata: {
      is_system_note: true,
    },
  },

  // ── Turn 9: GM Query (player asks Game Master directly) ──
  {
    id: "msg-015",
    adventure_id: "test-adventure",
    role: "user",
    content: "GM: What are my combat options? Can I flank the creature or should I try to intimidate it?",
    turn: 9,
    created_at: "2026-06-20T10:07:00Z",
    metadata: {
      is_gm_query: true,
      intent_classification: {
        primary_intent: "GM_QUERY",
        secondary_intents: [],
        advances_game: false,
        requires_dice: false,
        reasoning: "Player is asking the Game Master directly about rules and combat options.",
      },
    },
  },

  // ── Turn 9 response: GM reply ──
  {
    id: "msg-016",
    adventure_id: "test-adventure",
    role: "narrator",
    content: `🎲 **Game Master**

Good thinking. Here are your tactical options:

**Flank Attack** — Move to the creature's opposite side for advantage on your next attack roll. Elara is already engaging from the front, so flanking is possible. Roll with advantage.

**Intimidate** — You could try to scare it off with a show of force. Roll Intimidation (Charisma) vs DC 15. Success would make it hesitate; failure might enrage it.

**Called Shot** — Elara pointed out the eyes. A called shot has disadvantage but would blind the creature on a hit (AC 18 to hit the eyes specifically).

**Defensive** — Take the Dodge action and let Elara finish it. Safer, but less heroic.

The shadow-wolf is at 35/45 HP and wounded. One solid hit could turn the tide.`,
    turn: 9,
    created_at: "2026-06-20T10:07:10Z",
    metadata: {
      is_gm_query: true,
      credits_used: 0.0089,
      input_tokens: 340,
      output_tokens: 290,
    },
  },
];

/**
 * Adventure state stub matching what Play.tsx passes through adventureState.
 */
export const SAMPLE_ADVENTURE_STATE = {
  variables: {
    game_hour: 21,
    game_day: 3,
    day_phase: "night",
    current_weather: "heavy rain",
    current_season: "autumn",
    player_class: "ranger",
    player_level: 3,
  },
  current_location: "The Silver Tankard Inn",
  context_tags: ["combat", "storm", "inn"],
};
